import { useState } from 'react';
import { Box, Flex, IconButton } from '@chakra-ui/react';
import { Delete, Edit } from '@material-ui/icons';

import { TableBase } from '@app/components/common/table/table-base';
import { FrequencyVisitsView } from '@app/pages/customer-list/new-outlets/frequency-visit-view';

import { OutletRouteForm, OutletRouteRow } from '../types';
import { OutletRouteDialog } from '../modals/outlet-route';

export const OutletRoutesTable = ({
  routes,
  isLoading,
  onAddOutletRoute,
  onUpdateOutletRoute,
  onRemoveOutletRoute,
  readOnly = false,
}: {
  routes: OutletRouteRow[];
  isLoading: boolean;
  onAddOutletRoute: (item: OutletRouteForm) => Promise<boolean>;
  onUpdateOutletRoute: (routeNumberId: number, item: OutletRouteForm) => Promise<boolean>;
  onRemoveOutletRoute: (routeNumberId: number) => Promise<boolean>;
  readOnly?: boolean;
}) => {
  const [showForm, setShowForm] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(-1);

  const currentItem = routes[currentIndex];
  const isEdit = currentIndex !== -1;

  const onAddClick = () => {
    setShowForm(true);
    setCurrentIndex(-1);
  };

  const currentFormItem: OutletRouteForm | undefined = currentItem
    ? {
      route_number: {
        id: currentItem.route_number_id,
        name: currentItem.route_number_name,
      },
      route_number_id: currentItem.route_number_id,
      visits_day: currentItem.visits_day,
      is_active: currentItem.is_active,
    }
    : undefined;

  return (
    <Box mt={-5} marginX={-4}>
      <TableBase
        title="Список маршрутов"
        onCreateClick={!readOnly && onAddClick}
        columns={[
          {
            label: 'Маршрут',
            name: 'route_number_name',
            options: {
              sort: false,
              filter: false,
              customBodyRender: (
                value: OutletRouteRow['route_number_name'],
                { rowData }
              ) => (
                <Box whiteSpace="pre-wrap" style={{ wordBreak: 'break-all' }}>
                  {value?.toString()} [{rowData.route_number_id}]
                </Box>
              ),
            },
          },
          {
            label: 'День визита',
            name: 'visits_day',
            options: {
              sort: false,
              filter: false,
              customBodyRender: (value: OutletRouteRow['visits_day']) => (
                value ? <FrequencyVisitsView value={value} /> : null
              ),
            },
          },
          {
            label: 'Действия',
            name: 'route_number_id',
            options: {
              sort: false,
              filter: false,
              customBodyRender: (_, { rowIndex, rowData }) => (
                !readOnly ? (
                  <Flex gap={2} alignItems="center">
                    <IconButton
                      aria-label="edit"
                      onClick={() => {
                        setShowForm(true);
                        setCurrentIndex(rowIndex);
                      }}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      aria-label="delete"
                      onClick={() => {
                        onRemoveOutletRoute(rowData.route_number_id);
                      }}
                    >
                      <Delete />
                    </IconButton>
                  </Flex>
                ) : null
              ),
            },
          },
        ]}
        data={routes}
        loading={isLoading}
        paper={false}
      />
      <OutletRouteDialog
        isVisible={showForm}
        excludedRouteNumberIds={routes.map((item) => item.route_number_id)}
        item={currentFormItem}
        isEdit={isEdit}
        onClose={() => {
          setShowForm(false);
          setCurrentIndex(-1);
        }}
        onSave={async (item) => {
          if (isEdit && currentItem) {
            return onUpdateOutletRoute(currentItem.route_number_id, item);
          }

          return onAddOutletRoute(item);
        }}
      />
    </Box>
  );
};
